import { Skeleton, useToast } from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useGetUserActiveCreditFacilitiesQuery } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetLedgerAccountsQuery } from 'packages/statements/requests/ledger.gql.generated';
import { OutstandingLedgerTableContainer } from 'packages/statements/ui/OutstandingLedgerViewContainer';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

// Exports
type PortfolioStatementsOutstandingLedgerContainerProps = {
  creditFacilityId: string;
};
export const PortfolioStatementsOutstandingLedgerContainer = ({
  creditFacilityId,
}: PortfolioStatementsOutstandingLedgerContainerProps) => {
  const flags = useFeatureFlags();
  const toast = useToast();
  const navigate = useNavigate();

  const { data: userData } = useGetUserActiveCreditFacilitiesQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilitySlugsToHide: flags.creditFacilitySlugsToHide ?? [],
    },
  });
  const {
    data: ledgerAccountTypesData,
    loading: isLedgerAccountTypesLoading,
  } = useGetLedgerAccountsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
      ledgerAccountGroup: 'outstandingLedger',
    },
  });
  if (
    !userData ||
    !userData.user ||
    !ledgerAccountTypesData ||
    isLedgerAccountTypesLoading
  ) {
    return (
      <Skeleton
        data-testid="loading-portfolio-statements-outstanding-ledger"
        height="600px"
      />
    );
  }

  const {
    user: {
      organizationId,
      organization: { slug, name, creditFacilities },
    },
  } = userData;

  const userOrganization = { id: organizationId, slug, name };

  const creditFacilitySlug = _.find(
    creditFacilities,
    ({ id }) => id === creditFacilityId,
  )?.slug;

  if (!creditFacilityId || !creditFacilitySlug) {
    navigate('/portfolio/overview/facilities');
    toast({
      ...errorToastConfig,
      id: 'errorLoadPortfolioStatementsExpensesFeesNoCreditFacility',
      title: 'Please select a credit facility.',
    });
    return null;
  }

  const allAccountTypes = _.uniq(
    _.map(ledgerAccountTypesData.ledgerAccounts, 'tAccount'),
  );

  return (
    <OutstandingLedgerTableContainer
      accountTypes={allAccountTypes}
      capitalProviders={[userOrganization]}
      creditFacilityId={creditFacilityId}
    />
  );
};
